import {
  Component,
  OnDestroy,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { combineLatest, map, of, take } from 'rxjs';

import { ArcEndpointsService } from '@core/services/arc-endpoints/arc-endpoints.service';
import {
  GeoArcEditRequest,
  GeoArcEditSessionService,
  NEW_ARC_UID
} from '@core/services/geo-arc-edit-session/geo-arc-edit-session.service';
import { ICONS } from '@core/services/icons/icons.service';
import { SelectorsService } from '@core/services/selectors/selectors.service';
import { selectDataspaceState } from '@core/store/dataspace/dataspace.selectors';
import {
  ArcEndpoints,
  PolylineFormat,
  PolylineProblem,
  decodePath,
  encodePath,
  pathLengthKm,
  pathToSvgPath,
  validatePath
} from '@core/utils/geo-polyline.utils';

import { FormBuilderBase } from '@shared/components/row-edition-form/form-builder/form-builder.model';

import { ProfileType } from '@features/components/visualization/mapbox/models/map-data';
import {
  GeoArcRouteService,
  transportModeToProfile
} from '@features/components/visualization/mapbox/services/geo-arc-route.service';

const THUMB_WIDTH = 56;
const THUMB_HEIGHT = 40;

/**
 * `polyline_format` is a sibling control, not part of this field's value, and the front end
 * authors polyline5 only — so a written path must re-declare the format or the backend
 * rejects the save for a mismatch. Clearing the path leaves the declaration alone: there is
 * no geometry left for it to describe.
 */
export function writePolylineToForm(group: FormGroup | null, encoded: string | null): void {
  if (!group || !encoded) {
    return;
  }
  const format: AbstractControl | null = group.get('polyline_format');
  format?.setValue('Polyline5');
}

/**
 * A vehicle's geo_profile_cfg is typed as a bare object, so the transport mode is read
 * defensively rather than trusted to be present or to be a string.
 */
export function readTransportMode(raw: unknown): string | null {
  const cfg = (raw as { geo_profile_cfg?: { transport_mode?: unknown } } | undefined)
    ?.geo_profile_cfg;
  return typeof cfg?.transport_mode === 'string' ? cfg.transport_mode : null;
}

/**
 * The form-builder renders a field error as `'form.error.' + <first error key>`, handing the
 * error's *value* to the translate pipe as interpolation params — so the problem has to be
 * carried by the key, not by the value. ngx-translate runs a non-empty string value through
 * JSON.parse and throws from inside change detection, once per pass, for as long as the
 * invalid polyline is on screen.
 */
export function polylineErrors(
  problem: PolylineProblem | null
): Record<string, true> | null {
  switch (problem) {
    case 'undecodable':
      return { polylineUndecodable: true };
    case 'too-few-points':
      return { polylineTooFewPoints: true };
    case 'endpoints-too-far':
      return { polylineEndpointsTooFar: true };
    default:
      return null;
  }
}

/**
 * An arc being created has no name yet, and its uid is only the synthetic `NEW_ARC_UID`
 * sentinel — the session must show no label at all rather than surfacing that sentinel as a
 * panel title.
 */
export function sessionLabelFor(entityUid: string): string | undefined {
  return entityUid || undefined;
}

/**
 * Only a single named vehicle narrows the routing profile. An arc applying to a mixed fleet,
 * or to every vehicle, has no one transport mode to route by.
 */
export function vehicleUidForProfile(vehicleUids: string[]): string | null {
  return vehicleUids.length === 1 ? vehicleUids[0] : null;
}

@Component({
  selector: 'app-polyline',
  imports: [TranslatePipe],
  templateUrl: './polyline.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PolylineComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PolylineComponent),
      multi: true
    }
  ]
})
export class PolylineComponent implements ControlValueAccessor, Validator, OnDestroy {
  protected readonly ICONS = ICONS;
  protected readonly thumbWidth = THUMB_WIDTH;
  protected readonly thumbHeight = THUMB_HEIGHT;

  private readonly session = inject(GeoArcEditSessionService);
  private readonly endpointsService = inject(ArcEndpointsService);
  private readonly selectors = inject(SelectorsService);
  private readonly routeService = inject(GeoArcRouteService);
  private readonly container = inject(ControlContainer, { optional: true });
  private readonly store = inject(Store);
  private readonly dataspace = this.store.selectSignal(selectDataspaceState);
  private readonly translate = inject(TranslateService);

  id = input<FormBuilderBase['id']>();
  entityUid = input<string>('');

  protected readonly encoded = signal<string | null>(null);
  protected readonly isDisabled = signal(false);
  private readonly endpoints = signal<ArcEndpoints | null>(null);
  /**
   * The sibling controls are plain FormControls, so nothing about reading them is reactive.
   * Their values are mirrored into signals fed by valueChanges, because RowEditionFormComponent
   * builds its FormGroup once and only patches it afterwards — navigating between two arcs
   * reuses these controls, and anything derived from a one-off read stays on the first arc.
   */
  protected readonly format = signal<PolylineFormat>('Polyline5');
  protected readonly hasEndpointUids = signal(false);

  private get group(): FormGroup | null {
    return (this.container?.control as FormGroup | undefined) ?? null;
  }

  protected readonly path = computed(() => decodePath(this.encoded(), this.format()));
  protected readonly pointCount = computed(() => this.path().length);
  protected readonly lengthKm = computed(
    () => Math.round(pathLengthKm(this.path()) * 10) / 10
  );
  protected readonly thumbnailPath = computed(() =>
    pathToSvgPath(this.path(), THUMB_WIDTH, THUMB_HEIGHT)
  );
  protected readonly problem = computed(() => {
    // A stored string that decoded to nothing is unreadable in its declared format.
    // validatePath sees only the decoded path and cannot tell that from an absent one.
    if (this.encoded() && this.path().length === 0) {
      return 'undecodable';
    }
    return validatePath(this.path(), this.endpoints());
  });
  protected readonly isEmpty = computed(() => this.path().length === 0);
  /**
   * Marks the field before it has been touched, which is the only moment the form-builder's
   * error line stays silent — and the moment a stored polyline's own problems surface.
   */
  protected readonly hasError = computed(() => this.problem() !== null);

  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(map((event) => event.lang)),
    { initialValue: this.translate.getCurrentLang() }
  );

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  constructor() {
    const tail = this.group?.get('tail_site_uid');
    const head = this.group?.get('head_site_uid');

    this.hasEndpointUids.set(Boolean(tail?.value && head?.value));
    [tail, head].forEach((control) =>
      control?.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
        this.hasEndpointUids.set(Boolean(tail?.value && head?.value));
        // The resolved coordinates belong to the sites that were named a moment ago, so
        // keeping them would validate the path against an arc that no longer exists.
        this.setEndpoints(null);
        // Editing an endpoint invalidates the path: it starts and ends somewhere the arc
        // no longer names. Ending the session is honest; leaving it open is not.
        this.session.cancel();
      })
    );

    const format = this.group?.get('polyline_format');
    this.format.set((format?.value as PolylineFormat) ?? 'Polyline5');
    format?.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.format.set((value as PolylineFormat) ?? 'Polyline5');
      // The value was already validated against the previous format: a patch writes the
      // polyline before its declaration, so the errors on record describe the wrong codec
      // until Angular is told to ask again.
      this.onValidatorChange();
    });

    // Re-registers whenever the gating state changes, rather than once, so the button's
    // disabled state tracks it. It must stay disabled while a session is active: beginning
    // a second session resets vias to [] and silently discards via edits made on the map
    // but not yet committed.
    effect(() => {
      this.currentLang();
      this.session.registerToolboxAction({
        id: 'compute-polyline',
        icon: ICONS.flowArrow,
        tooltip: this.translate.instant('features.entities.geo_arc.polyline.compute'),
        disabled: !this.hasEndpointUids() || this.session.isActive(),
        callback: () => this.onCompute()
      });
    });
  }

  ngOnDestroy(): void {
    this.session.registerToolboxAction(null);
    this.session.cancel();
  }

  writeValue(value: string | null): void {
    this.encoded.set(value ?? null);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  validate(): Record<string, true> | null {
    return polylineErrors(this.problem());
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  /**
   * Builds the request that opens an edit-on-map session. `compute` is not called here — it
   * is handed to the session, which calls it later, once per via change, while the session
   * runs.
   */
  private sessionRequest(endpoints: ArcEndpoints, profile: ProfileType): GeoArcEditRequest {
    return {
      uid: this.entityUid() || NEW_ARC_UID,
      label: sessionLabelFor(this.entityUid()),
      path: this.path(),
      endpoints,
      compute: (points) => this.routeService.computePath(points, profile),
      onCommit: (path) => this.commit(path)
    };
  }

  protected onEditOnMap(): void {
    this.onTouched();
    this.resolveContext((endpoints, profile) => {
      // A session without endpoints cannot route: every drag is refused by applyVias and a
      // path-less arc has nothing to draw, so the panel would open onto a dead map.
      if (!endpoints) {
        return;
      }
      this.session.begin(this.sessionRequest(endpoints, profile));
    });
  }

  protected onCompute(): void {
    this.onTouched();
    this.resolveContext((endpoints, profile) => {
      // Nothing to route between; the field already says so beside the button.
      if (!endpoints) {
        return;
      }
      this.session.begin(this.sessionRequest(endpoints, profile));
      void this.session.recompute();
    });
  }

  protected onClear(): void {
    this.onTouched();
    this.commit([]);
  }

  private commit(path: { lat: number; lng: number }[]): void {
    const encoded = path.length > 0 ? encodePath(path) : null;
    this.encoded.set(encoded);
    writePolylineToForm(this.group, encoded);
    this.onChange(encoded);
  }

  /** Resolves the endpoints and the routing profile together, since a session needs both. */
  private resolveContext(
    then: (endpoints: ArcEndpoints | null, profile: ProfileType) => void
  ): void {
    const dataSpaceId = this.dataspace().dataspace?.id;
    if (!dataSpaceId) {
      then(null, ProfileType.CAR);
      return;
    }
    const vehicles = (this.group?.get('vehicle_uids')?.value as string[]) ?? [];
    const vehicleUid = vehicleUidForProfile(vehicles);
    const mode$ = vehicleUid
      ? this.selectors
          .selectEntityByUid$('VEHICLE', 'UserVehicle', vehicleUid, dataSpaceId)
          .pipe(map((entity) => readTransportMode(entity?.raw)))
      : of(null);

    combineLatest([
      this.endpointsService.resolve$(
        this.group?.get('tail_site_uid')?.value ?? null,
        this.group?.get('head_site_uid')?.value ?? null,
        dataSpaceId
      ),
      mode$
    ])
      .pipe(take(1))
      .subscribe(([endpoints, mode]) => {
        this.setEndpoints(endpoints);
        then(endpoints, transportModeToProfile(mode));
      });
  }

  /**
   * `problem()` reads this signal, but `validate()` is only re-run when Angular is told the
   * validator's own inputs changed. Without the notification the control keeps the errors it
   * computed while the sites were still unresolved, so Save stays enabled on a path the
   * backend will reject even though the field is already showing it as invalid.
   */
  private setEndpoints(endpoints: ArcEndpoints | null): void {
    if (this.endpoints() === endpoints) {
      return;
    }
    this.endpoints.set(endpoints);
    this.onValidatorChange();
  }
}
